import React from "react";
import { makeStyles } from "@material-ui/core";
import { BookingProfileFragment, BookingWeekCardFragment, CustomerType } from "@graphql";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { store } from "@store";
import { Body, PlainLink } from "@components";
import ItemCard from "@components/ItemCard/ItemCard";
import CustomerChip from "@components/CustomerChip";
import CustomerParents from "@components/CustomerParents";
import { CustomerProfileLocation } from "@navigation/locations/CustomerProfileLocation";
import { ConsultantProfileLocation } from "@navigation/locations/consultant-profile.location";
import { CONSULTANT_SLIDER } from "@navigation";
import ConsultantCard from "@consultants/ConsultantCard/ConsultantCard";
import NationellaChip from "@common/NationellaChip";
import { erpExportUnitUrl, erpWorkRequestUrl } from "../../erp/erp-links";

type Props = {
  item: BookingWeekCardFragment | BookingProfileFragment;
  onOpen?: () => any;
  onEdit?: () => any;
};

export function BookingWeekCard(props: Props) {
  const { item } = props;
  const classes = useStyles({});

  const openCustomer = (id: string) =>
    store().navigation.open("customer_slider", new CustomerProfileLocation(id));

  const openConsultant = (id: string) =>
    store().navigation.open(CONSULTANT_SLIDER, new ConsultantProfileLocation(id));

  const callNumber = item.callNumber;

  const subheading = (
    <div>
      <Body style={{ fontWeight: "bold" }}>
        {item.BookingGroup.hospital.Address?.Region || ""}
      </Body>
      <CustomerParents
        parents={item.BookingGroup.hospital.Parents}
        onClick={(e, id) => {
          e.preventDefault();
          openCustomer(id);
        }}
        afterLabel={
          <span style={{ marginLeft: 8 }}>
            <CustomerChip type={CustomerType.Region} />
          </span>
        }
      />
    </div>
  );

  const departments = () => {
    if (!item.BookingGroup.department) {
      return null;
    }

    return (
      <div className={classes.departmentList}>
        <PlainLink
          className={classes.departmentLink}
          key={item.BookingGroup.department._id}
          onClick={(e) => {
            e.preventDefault();
            openCustomer(item.BookingGroup.department._id);
          }}
        >
          {item.BookingGroup.department.Name}
        </PlainLink>
        <CustomerChip type={CustomerType.Department} />
      </div>
    );
  };

  // Imported bookings carry the id of the source they came from — today that is
  // an ERP work request on the group and an ERP export unit on the week, each
  // linking into ERP. Bookings created in CRM have neither: row dropped.
  const externalId = item.BookingGroup.externalId;
  // Resolved server-side, so a month-break counterpart links to the same
  // export unit as the first-month booking it was split off.
  const exportUnitRef = item.erpExportUnitRef;

  const erpLinks = () => {
    if (!externalId && !exportUnitRef) {
      return null;
    }

    return (
      <>
        {externalId && (
          <Body block className={classes.detailRow}>
            {locale.erpWorkRequest}:{" "}
            <a
              href={erpWorkRequestUrl(externalId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {locale.openInErp}
            </a>
          </Body>
        )}
        {exportUnitRef && (
          <Body block className={classes.detailRow}>
            {locale.erpExportUnit}:{" "}
            <a
              href={erpExportUnitUrl(exportUnitRef)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {locale.openInErp}
            </a>
          </Body>
        )}
      </>
    );
  };

  const subtitle = (
    <div className={classes.details}>
      {departments()}

      <Body block className={classes.detailRow}>
        {locale.Expertise}:{" "}
        <span className={classes.detailValue}>{item.AreaOfExpertise}</span>
      </Body>

      <Body block className={classes.detailRow}>
        {locale.CustomerNumberShort}:{" "}
        <span className={classes.detailValue}>{item.CustomerNumber || "n/a"}</span>
      </Body>

      <Body block className={classes.detailRow}>
        {locale.CallNumber}:{" "}
        <span className={classes.detailValue}>{callNumber || "n/a"}</span>
      </Body>

      {erpLinks()}

      <Body block className={classes.detailRow}>
        {locale.RecruitingPortalType}:{" "}
        <span className={classes.detailValue}>
          {locale.recruitingPortalType[
            item.BookingGroup.hospital?.RecruitingPortalType
          ] ?? "n/a"}
        </span>
      </Body>
    </div>
  );

  const chips = (
    <>
      <CustomerChip type={CustomerType.Hospital} />
      {(item as BookingProfileFragment).BookingGroup?.isNationalProcurement && (
        <NationellaChip />
      )}
    </>
  );

  return (
    <ItemCard
      entity={{ Name: item.BookingGroup.hospital.Name }}
      title={item.BookingGroup.hospital.Name}
      chips={chips}
      subTitle={subtitle}
      subheading={subheading}
      onTitleClick={(e) => {
        e.preventDefault();
        openCustomer(item.BookingGroup.hospital._id);
      }}
      withRightPane={
        <ConsultantCard
          consultantId={item.Consultant._id}
          details={[]}
          flat
          style={{ background: "#fff" }}
          onEdit={props.onEdit}
          onTitleClick={(e) => {
            e.preventDefault();
            openConsultant(item.Consultant._id);
          }}
        />
      }
      onClick={props.onOpen}
    />
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  avatar: {
    minWidth: 48
  },

  nameMarksWrapper: {
    alignItems: "center",
    display: "flex"
  },

  details: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start"
  },

  detailRow: {
    display: "block",
    fontWeight: "bold",
    lineHeight: 1.6
  },

  detailValue: {
    fontWeight: "normal"
  },

  departmentList: {
    marginBottom: theme.spacing(1 / 2),
    ...theme.mixins.childrenMarginRight(theme.spacing(1 / 2))
  },

  departmentLink: {
    display: "inline"
  },

  header: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(2)
  },

  headerText: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 10
  }
}));
